import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useMergeStyles } from "@thkStore/Overrides/additionalStyles";
import React, { useEffect } from "react";
import { css } from "styled-components";

// SRI is intentionally not used: the Dirxion CDN URL is not version-pinned, so any
// upstream update would break the page silently. Trust scope: Dirxion is the catalog
// vendor whose iframe we embed, so a compromised CDN already implies a compromised
// catalog — pinning the hash adds no defense and would create a fragile dependency.
const DIRXION_SCRIPT_SRC = "https://ssl.dirxion.com/embed/DirxionEmbed.min.thk.js";
const DIRXION_SCRIPT_FLAG = "data-dirxion-embed-loaded";
const DIRXION_IFRAME_ID = "DirxionPubIframe";
const QUICKVIEW_MAX_RETRIES = 10;
const QUICKVIEW_RETRY_MS = 500;

const enum fields {
    catalogUrl = "catalogUrl",
    iframeTitle = "iframeTitle",
    heightMode = "heightMode",
    fixedHeightPx = "fixedHeightPx",
    viewportOffsetPx = "viewportOffsetPx",
    allowFullscreen = "allowFullscreen",
    enableQuickviewBridge = "enableQuickviewBridge",
}

export interface DirxionCatalogWidgetStyles {
    iframeWrapper?: InjectableCss;
    emptyConfigWrapper?: InjectableCss;
    emptyConfigTitle?: TypographyProps;
    emptyConfigMessage?: TypographyProps;
    shellWrapper?: InjectableCss;
    shellTitle?: TypographyProps;
    shellUrl?: TypographyProps;
    shellNote?: TypographyProps;
}

export const dirxionCatalogWidgetStyles: DirxionCatalogWidgetStyles = {
    iframeWrapper: {
        css: css`
            width: 100%;
            iframe {
                display: block;
                width: 100%;
                border: none;
            }
            @media (max-width: 768px) {
                iframe { min-height: 600px; }
            }
        `,
    },
    emptyConfigWrapper: {
        css: css`
            background: #f9f9f9;
            padding: 24px;
            border: 1px dashed #ccc;
            text-align: center;
        `,
    },
    emptyConfigTitle: {
        variant: "h5",
        css: css`margin: 0 0 8px 0;`,
    },
    emptyConfigMessage: {},
    shellWrapper: {
        css: css`
            background: #f0f0f0;
            padding: 16px;
            border: 1px dashed #ccc;
        `,
    },
    shellTitle: {
        variant: "h6",
        css: css`margin: 0 0 8px 0;`,
    },
    shellUrl: {
        css: css`word-break: break-all;`,
    },
    shellNote: {
        css: css`margin-top: 8px; font-style: italic;`,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.catalogUrl]: string;
        [fields.iframeTitle]: string;
        [fields.heightMode]: "viewport" | "fixed";
        [fields.fixedHeightPx]: number;
        [fields.viewportOffsetPx]: number;
        [fields.allowFullscreen]: boolean;
        [fields.enableQuickviewBridge]: boolean;
    };
    extendedStyles?: DirxionCatalogWidgetStyles;
}

type Props = OwnProps & HasShellContext;

let embedScriptPromise: Promise<void> | null = null;

const loadDirxionEmbedScript = (): Promise<void> => {
    if (embedScriptPromise) {
        return embedScriptPromise;
    }
    if (document.querySelector(`script[${DIRXION_SCRIPT_FLAG}]`)) {
        embedScriptPromise = Promise.resolve();
        return embedScriptPromise;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.charset = "UTF-8";
    script.src = DIRXION_SCRIPT_SRC;
    script.setAttribute(DIRXION_SCRIPT_FLAG, "true");
    embedScriptPromise = new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => {
            embedScriptPromise = null;
            reject(new Error("Failed to load DirxionEmbed.min.js"));
        };
    });
    document.head.appendChild(script);
    return embedScriptPromise;
};

const isHttpsUrl = (value: string): boolean => /^https:\/\//i.test(value);

const DirxionCatalogWidget: React.FC<Props> = ({ fields, shellContext, extendedStyles }) => {
    const styles = useMergeStyles("dirxionCatalog", dirxionCatalogWidgetStyles, extendedStyles);
    const urlIsValid = isHttpsUrl(fields.catalogUrl);

    useEffect(() => {
        if (IS_SERVER_SIDE || shellContext.isInShell || !urlIsValid) {
            return;
        }

        let cancelled = false;
        let timerId: number | undefined;

        loadDirxionEmbedScript().catch(err => {
            if (!cancelled) {
                console.error("[DirxionCatalog] script load failed", err);
            }
        });

        if (fields.enableQuickviewBridge) {
            let retries = 0;
            const tryRegister = () => {
                if (cancelled) {
                    return;
                }
                const initHandler = (window as any).DirxionInitReceiveMessageHandler;
                if (typeof initHandler === "function") {
                    initHandler((_msg: any) => {
                        // No-op until the pid → product mapping is defined in a follow-up ticket.
                    });
                    return;
                }
                retries += 1;
                if (retries >= QUICKVIEW_MAX_RETRIES) {
                    console.warn("[DirxionCatalog] DirxionInitReceiveMessageHandler never appeared; giving up.");
                    return;
                }
                timerId = window.setTimeout(tryRegister, QUICKVIEW_RETRY_MS);
            };
            tryRegister();
        }

        return () => {
            cancelled = true;
            if (timerId !== undefined) {
                window.clearTimeout(timerId);
            }
        };
    }, [fields.catalogUrl, fields.enableQuickviewBridge, shellContext.isInShell, urlIsValid]);

    if (!fields.catalogUrl || !urlIsValid) {
        const message = !fields.catalogUrl
            ? translate("Catalog URL is not configured. Open this page in the CMS and set the Catalog URL field.")
            : translate("Catalog URL must use https://. Update the field in the CMS.");
        return (
            <StyledWrapper {...styles.emptyConfigWrapper}>
                <Typography {...styles.emptyConfigTitle}>
                    {translate("Digital Catalog")}
                </Typography>
                <Typography {...styles.emptyConfigMessage}>{message}</Typography>
            </StyledWrapper>
        );
    }

    if (shellContext.isInShell && shellContext.isEditing) {
        return (
            <StyledWrapper {...styles.shellWrapper}>
                <Typography {...styles.shellTitle}>
                    {translate("Dirxion Catalog Widget")}
                </Typography>
                <Typography {...styles.shellUrl}>
                    {fields.catalogUrl}
                </Typography>
                <Typography {...styles.shellNote}>
                    {translate("The catalog iframe renders on the live storefront, not in the CMS editor.")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (IS_SERVER_SIDE) {
        return <div data-dirxion-placeholder />;
    }

    const height = fields.heightMode === "fixed"
        ? `${fields.fixedHeightPx || 900}px`
        : `calc(100vh - ${fields.viewportOffsetPx || 120}px)`;

    return (
        <StyledWrapper {...styles.iframeWrapper}>
            <iframe
                id={DIRXION_IFRAME_ID}
                src={fields.catalogUrl}
                title={fields.iframeTitle || "Digital Catalog"}
                allowFullScreen={fields.allowFullscreen}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                style={{ height }}
            />
        </StyledWrapper>
    );
};

const basicTab = { displayName: "Basic", sortOrder: 0 };
const layoutTab = { displayName: "Layout", sortOrder: 1 };
const advancedTab = { displayName: "Advanced", sortOrder: 2 };

const widgetModule: WidgetModule = {
    component: withIsInShell(DirxionCatalogWidget),
    definition: {
        group: "Basic",
        icon: "book",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.catalogUrl,
                editorTemplate: "TextField",
                displayName: "Catalog URL",
                defaultValue: "",
                tooltip: "Full URL of the Dirxion publication (must start with https://).",
                tab: basicTab,
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return /^https:\/\//i.test(value)
                        ? null
                        : "Catalog URL must start with https://";
                },
            },
            {
                fieldType: "General",
                name: fields.iframeTitle,
                editorTemplate: "TextField",
                displayName: "Iframe Title",
                defaultValue: "Digital Catalog",
                tooltip: "Accessible title for the iframe (screen readers).",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.allowFullscreen,
                editorTemplate: "CheckboxField",
                displayName: "Allow Fullscreen",
                defaultValue: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.heightMode,
                editorTemplate: "DropDownField",
                displayName: "Height Mode",
                options: [
                    { displayName: "Viewport (responsive)", value: "viewport" },
                    { displayName: "Fixed (pixels)", value: "fixed" },
                ],
                defaultValue: "viewport",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.viewportOffsetPx,
                editorTemplate: "IntegerField",
                displayName: "Viewport Offset (px)",
                defaultValue: 120,
                tooltip: "Pixels subtracted from 100vh to leave room for header/footer. Only used when Height Mode is Viewport.",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.fixedHeightPx,
                editorTemplate: "IntegerField",
                displayName: "Fixed Height (px)",
                defaultValue: 900,
                tooltip: "Iframe height in pixels. Only used when Height Mode is Fixed.",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.enableQuickviewBridge,
                editorTemplate: "CheckboxField",
                displayName: "Enable Quickview Bridge (experimental)",
                defaultValue: false,
                tooltip: "Registers a postMessage handler for catalog → storefront events. Currently a no-op; do not enable until the product-resolution logic is implemented.",
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
